/**
 * Read the FINU (BEP-20) token balance directly from the BSC chain via a public
 * RPC node — no backend round-trip and no Infura secret needed. `balanceOf` is
 * public on-chain data, so the client reads it itself using a raw JSON-RPC
 * `eth_call` (no web3 library dependency).
 */

const RPC_URL = import.meta.env.VITE_BSC_RPC_URL || 'https://bsc-rpc.publicnode.com';
const FINU_TOKEN_ADDRESS = (import.meta.env.VITE_FINU_TOKEN_ADDRESS || '0xa95b8ffb6ba053f86912b306af9f86e75643a8c2').toLowerCase();
const NETWORK = import.meta.env.VITE_FINU_NETWORK || 'bsc-mainnet';

const BALANCE_OF_SELECTOR = '0x70a08231'; // keccak("balanceOf(address)")[:4]
const DECIMALS_SELECTOR = '0x313ce567'; // keccak("decimals()")[:4]

const padAddress = (addr) => String(addr).toLowerCase().replace(/^0x/, '').padStart(64, '0');

const ethCall = async (to, data, signal) => {
    const res = await fetch(RPC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'eth_call',
            params: [{ to, data }, 'latest'],
        }),
        signal,
    });
    if (!res.ok) throw new Error(`RPC HTTP ${res.status}`);
    const json = await res.json();
    if (json.error) throw new Error(json.error.message || 'RPC error');
    return json.result; // hex string
};

/**
 * Fetch the FINU balance for a wallet address straight from chain.
 * Returns the same shape the old backend endpoint did, so callers don't change:
 *   { balance, token_address, wallet_address, network }
 */
export const getFinuBalanceOnchain = async (address, { timeoutMs = 8000 } = {}) => {
    if (!address) {
        return {
            balance: 0,
            token_address: FINU_TOKEN_ADDRESS,
            wallet_address: address || null,
            network: NETWORK,
        };
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const [balHex, decHex] = await Promise.all([ethCall(FINU_TOKEN_ADDRESS, BALANCE_OF_SELECTOR + padAddress(address), controller.signal), ethCall(FINU_TOKEN_ADDRESS, DECIMALS_SELECTOR, controller.signal)]);

        const raw = BigInt(balHex && balHex !== '0x' ? balHex : '0x0');
        const decimals = decHex && decHex !== '0x' ? Number(BigInt(decHex)) : 18;
        const balance = Number(raw) / 10 ** decimals;

        return {
            balance,
            token_address: FINU_TOKEN_ADDRESS,
            wallet_address: address,
            network: NETWORK,
        };
    } finally {
        clearTimeout(timer);
    }
};
